import React, {
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { DataTable as PrimeDataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { useTranslation } from 'react-i18next';
import { Dropdown } from 'primereact/dropdown';
import { Icon } from '../Icon';
import { FilterMatchMode, FilterService } from 'primereact/api';
import { InputText } from 'primereact/inputtext';
import cn from 'classnames';
import styles from './DataTable.styles.scss';
import TableFilters from '../TableFilters/TableFilters';
import FilterChipsDisplay from '../TableFilters/FilterChipsDisplay';
import ColumnFilterChipsDisplay from './ColumnFilterChipsDisplay';
import { filterWithSortTemplate, DATE_SORT_LABELS } from './filterComponents';
import { clearColumnFilter, clearAllColumnFilters } from './columnHelpers';

/**
 * Generic DataTable Component with Filter & Sort Support
 *
 * How to enable sort in column filters:
 * 1. Pass showSortInFilters={true} to the DataTable component
 * 2. Add showSortInFilter: true to any column definition
 * 3. That's it! Sort buttons will automatically appear in the filter menu
 *
 * Example:
 * <DataTable
 *   showSortInFilters={true}
 *   columns={[
 *     {
 *       label: 'common.name',
 *       value: 'name',
 *       filter: true,
 *       showSortInFilter: true,  // Enable sort for this column
 *       filterElement: textFilterTemplate,
 *       filterMatchMode: FilterMatchMode.CONTAINS,
 *     }
 *   ]}
 *   // ... other props
 * />
 *
 * For even simpler setup, use the column helpers:
 * import { createTextColumn, createDateRangeColumn } from './columnHelpers';
 *
 * columns={[
 *   createTextColumn('name', 'common.name', { showSortInFilter: true }),
 *   createDateRangeColumn('date', 'common.date', { showSortInFilter: true })
 * ]}
 */
const DataTable = forwardRef(
  (
    {
      loading,
      selectable = false,
      selectedRows = [],
      setSelectedRows,
      columns = [],
      dataKey,
      value,
      exportFilename,
      filters,
      filtersConfig,
      searchConfig,
      searchFields,
      onResetFilters,
      onSubmitFilters,
      convertDataToForm,
      isDataSelectable,
      rowClassName,
      cellClassName,
      size = 'small',
      rows = 10,
      removableSort = false,
      alwaysShowPaginator = true,
      showIndex = false,
      filterDisplay,
      emptyMessage,
      beforeFilters,
      afterFilters,
      showAdvancedFilters = false,
      exportFunction,
      showSortInFilters = false, // Set to true to enable sort options in column filters
      showAdvancedFiltersChips = true,
      responsiveLayout = 'scroll', // 'scroll' or 'stack'
      breakpoint = '960px', // Breakpoint for responsive layout
      onValueChange,
      noDataDesc,
      noDataTitle,
      rowsPerPageOptions = [10, 20, 50, 100],
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const filterFormRef = useRef();

    const normalizeFilters = useCallback((nextFilters) => {
      const normalized = { ...(nextFilters || {}) };

      normalized.global = normalized.global || {
        value: null,
        matchMode: FilterMatchMode.CONTAINS,
      };

      Object.entries(normalized).forEach(([field, meta]) => {
        if (field === 'global') {
          return;
        }
        if (!meta) {
          delete normalized[field];
          return;
        }

        if (Array.isArray(meta.constraints)) {
          const constraints = meta.constraints.filter(Boolean).map((c) => ({
            value: c.value ?? null,
            matchMode: c.matchMode,
          }));

          normalized[field] = {
            ...meta,
            constraints:
              constraints.length > 0
                ? constraints
                : [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
          };
          return;
        }

        normalized[field] = {
          ...meta,
          value: 'value' in meta ? meta.value ?? null : null,
        };
      });

      return normalized;
    }, []);

    const [globalFilterValue, setGlobalFilterValue] = useState('');
    const [filterFormState, setFilterFormState] = useState({});
    const [sortField, setSortField] = useState(null);
    const [sortOrder, setSortOrder] = useState(null);
    const [currentRows, setCurrentRows] = useState(rows);
    const [tableFilters, setTableFilters] = useState(() =>
      normalizeFilters(
        searchConfig || {
          global: { value: null, matchMode: FilterMatchMode.CONTAINS },
        },
      ),
    );

    // PrimeReact only auto-registers CUSTOM matchers when it owns filter state.
    // Since this wrapper provides controlled filters, we must register them.
    useEffect(() => {
      const columnDefs =
        typeof columns === 'function' ? columns({ t }) : columns;

      (Array.isArray(columnDefs) ? columnDefs : []).forEach((col) => {
        if (!col) {
          return;
        }

        const field = col.filterField || col.value;
        if (!field) {
          return;
        }

        if (col.filterMatchMode !== FilterMatchMode.CUSTOM) {
          return;
        }
        if (typeof col.filterFunction !== 'function') {
          return;
        }

        FilterService.register(`custom_${field}`, (...args) =>
          col.filterFunction(...args, { column: col }),
        );
      });
    }, [columns, t]);

    // Sync currentRows with rows prop when it changes
    useEffect(() => {
      setCurrentRows(rows);
    }, [rows]);

    const onGlobalFilterChange = useCallback(
      (e) => {
        const val = e.target.value;
        let _filters = {
          ...tableFilters,
          global: tableFilters.global || {
            value: null,
            matchMode: FilterMatchMode.CONTAINS,
          },
        };
        _filters.global.value = val;
        setTableFilters(_filters);
        setGlobalFilterValue(val);
      },
      [tableFilters],
    );

    const globalSearchInput = (
      <>
        <span className="pi pi-search" />
        <InputText
          type="text"
          placeholder={t('common.search')}
          value={globalFilterValue}
          onChange={onGlobalFilterChange}
          icon="pi pi-chevron-down"
        />
      </>
    );

    const rowsPerPageDropdown = useCallback(
      (options) => {
        if (
          ref?.current
            .getTable()
            .querySelector('tbody tr.p-datatable-emptymessage')
        ) {
          return null;
        }
        const dropdownOptions = rowsPerPageOptions.map((option) => ({
          label: option,
          value: option,
        }));
        return (
          <div className="items-per-page">
            {t('show')}
            <Dropdown
              value={options.value}
              options={dropdownOptions}
              onChange={(e) => {
                setCurrentRows(e.value);
                options.onChange(e);
              }}
            />
            {t('entries')}
          </div>
        );
      },
      [t],
    );

    const nextPageLink = useCallback(
      (options) => {
        if (
          ref?.current
            .getTable()
            .querySelector('tbody tr.p-datatable-emptymessage') ||
          value.length <= currentRows
        ) {
          return null;
        }
        return <div className="next-page">{options.element}</div>;
      },
      [value.length, currentRows],
    );

    const prevPageLink = useCallback(
      (options) => {
        if (
          ref?.current
            .getTable()
            .querySelector('tbody tr.p-datatable-emptymessage') ||
          value.length <= currentRows
        ) {
          return null;
        }
        return <div className="prev-page">{options.element}</div>;
      },
      [value.length, currentRows],
    );

    const pageLinks = useCallback(
      (options) => {
        if (
          ref?.current
            .getTable()
            .querySelector('tbody tr.p-datatable-emptymessage') ||
          value.length <= currentRows
        ) {
          return null;
        }
        return <div className="page-lnk">{options.element}</div>;
      },
      [value.length, currentRows],
    );

    const columnBody = useCallback(
      (_data, options) => options.rowIndex + 1,
      [],
    );

    const handleSort = useCallback((field, order) => {
      setSortField(field);
      setSortOrder(order);
    }, []);

    const filterClearTemplate = useCallback(
      (options) => {
        return (
          <button
            type="button"
            className="p-button p-component p-button-outlined p-button-sm flex-1"
            onClick={() => {
              options.filterClearCallback();
              // Also clear sort when resetting filters
              setSortField(null);
              setSortOrder(null);
            }}
          >
            {t('common.reset')}
          </button>
        );
      },
      [t],
    );

    const filterApplyTemplate = useCallback(
      (options) => {
        return (
          <button
            type="button"
            className="p-button p-component p-button-sm flex-1"
            onClick={options.filterApplyCallback}
          >
            {t('common.apply_now')}
          </button>
        );
      },
      [t],
    );

    const handleClearColumnFilter = useCallback(
      (field, constraintIndex) => {
        setTableFilters((prevFilters) =>
          normalizeFilters(
            clearColumnFilter(prevFilters, field, constraintIndex),
          ),
        );
      },
      [normalizeFilters],
    );

    const handleClearAllColumnFilters = useCallback(() => {
      setTableFilters((prevFilters) =>
        normalizeFilters(clearAllColumnFilters(prevFilters)),
      );
      setSortField(null);
      setSortOrder(null);
    }, [normalizeFilters]);

    // Check if any filters are active
    const hasActiveFilters = useCallback(() => {
      // Check global filter
      if (globalFilterValue && globalFilterValue.trim() !== '') {
        return true;
      }

      // Check column filters
      if (tableFilters) {
        const filterKeys = Object.keys(tableFilters).filter(
          (key) => key !== 'global',
        );

        for (const key of filterKeys) {
          const filter = tableFilters[key];

          // Check for constraints-based filters
          if (filter?.constraints && Array.isArray(filter.constraints)) {
            const hasValue = filter.constraints.some(
              (constraint) =>
                constraint?.value !== null &&
                constraint?.value !== undefined &&
                constraint?.value !== '',
            );
            if (hasValue) {
              return true;
            }
          }

          // Check for value-based filters
          if (
            filter?.value !== null &&
            filter?.value !== undefined &&
            filter?.value !== ''
          ) {
            return true;
          }
        }
      }

      // Check advanced filters
      if (filterFormState && Object.keys(filterFormState).length > 0) {
        const hasValue = Object.values(filterFormState).some(
          (filterValue) =>
            filterValue !== null &&
            filterValue !== undefined &&
            filterValue !== '' &&
            !(Array.isArray(filterValue) && filterValue.length === 0),
        );
        if (hasValue) {
          return true;
        }
      }

      return false;
    }, [globalFilterValue, tableFilters, filterFormState]);

    return (
      <div className={cn(styles.container, 'data-table')}>
        <div className="d-flex flex-row-reverse align-items-center gap-2 mb-4">
          {beforeFilters?.({ ref })}
          {showAdvancedFilters && filters && (
            <TableFilters
              ref={filterFormRef}
              onResetFormClick={onResetFilters}
              onSubmitFilters={(values) => {
                setFilterFormState(values);
                onSubmitFilters(values);
              }}
              filters={filters}
              filtersConfig={filtersConfig}
              convertDataToForm={convertDataToForm}
            >
              {searchFields && (
                <div className={styles.searchWrapper}>{globalSearchInput}</div>
              )}
            </TableFilters>
          )}
          {showAdvancedFilters && !filters && searchFields && searchFields && (
            <div className={cn(styles.searchWrapper, 'col-4 mb-4')}>
              {globalSearchInput}
            </div>
          )}
          {afterFilters?.({ ref })}
        </div>

        {showAdvancedFilters && showAdvancedFiltersChips && filters && (
          <div className="d-flex gap-3 mb-4">
            <FilterChipsDisplay
              filtersConfig={filtersConfig}
              values={filterFormState}
              initialValues={convertDataToForm([])}
              submitCount={
                filterFormRef.current?.formRef?.current?.submitCount || 0
              }
              filterValues={filterFormState}
              formRef={filterFormRef.current?.formRef}
              onResetFormClick={() => {
                onResetFilters(
                  filterFormRef.current?.formRef?.current?.resetForm,
                );
                setFilterFormState({});
              }}
            />
          </div>
        )}

        <ColumnFilterChipsDisplay
          filters={tableFilters}
          columns={columns}
          onClearFilter={handleClearColumnFilter}
          onClearAllFilters={handleClearAllColumnFilters}
        />

        <PrimeDataTable
          ref={ref}
          tableClassName={styles.table}
          exportFunction={exportFunction}
          value={value}
          size={size}
          dataKey={dataKey}
          loading={loading}
          alwaysShowPaginator={alwaysShowPaginator}
          globalFilterFields={searchFields}
          filters={tableFilters}
          onFilter={(e) => setTableFilters(normalizeFilters(e.filters))}
          filterDisplay={filterDisplay}
          sortField={sortField}
          sortOrder={sortOrder}
          onSort={(e) => {
            setSortField(e.sortField);
            setSortOrder(e.sortOrder);
          }}
          responsiveLayout={responsiveLayout}
          breakpoint={breakpoint}
          emptyMessage={
            emptyMessage ??
            (hasActiveFilters() ? (
              <div className="align-items-center d-flex flex-column gap-3 my-5 text-black">
                <Icon className="mb-3" name={'notFound'} />
                <h3 className="fw-bold">
                  {t('common.no_matches_found_title')}
                </h3>
                <p className="lh-base">{t('common.no_matches_found_desc')}</p>
              </div>
            ) : (
              <div className="align-items-center d-flex flex-column gap-3 my-5 text-black">
                <Icon className="mb-3" name={'noData'} />
                <h3 className="fw-bold">
                  {noDataTitle ?? t('common.no_data_title')}
                </h3>
                <p className="lh-base">
                  {noDataDesc ?? t('common.no_data_desc')}
                </p>
              </div>
            ))
          }
          onValueChange={onValueChange}
          paginator={value.length}
          rowHover={false}
          rows={currentRows}
          rowsPerPageOptions={rowsPerPageOptions}
          selectionMode={'checkbox'}
          selection={selectedRows}
          onSelectionChange={(e) => setSelectedRows?.(e.value)}
          exportFilename={exportFilename}
          stripedRows
          removableSort={removableSort}
          headerTooltipOptions={{ position: 'top' }}
          isDataSelectable={isDataSelectable}
          rowClassName={rowClassName}
          cellClassName={cellClassName}
          paginatorTemplate={{
            RowsPerPageDropdown: rowsPerPageDropdown,
            // CurrentPageReport: currentPageReport,
            NextPageLink: nextPageLink,
            PrevPageLink: prevPageLink,
            PageLinks: pageLinks,
          }}
        >
          {selectable && (
            <Column selectionMode="multiple" headerStyle={{ width: '3rem' }} />
          )}
          {showIndex && (
            <Column
              header="#"
              headerStyle={{ width: '3rem' }}
              body={columnBody}
            />
          )}
          {columns({ t, sortField, sortOrder }).map((column) => {
            const isColumnSorted = sortField === column.value;
            const defaultHeader = () => {
              const headerText = t(column.label);
              const headerTextHyphenated = headerText.replace('-', '⁠-⁠');
              return (
                <div className="d-flex align-items-center justify-content-center gap-2">
                  <span>{headerTextHyphenated}</span>
                  {isColumnSorted && (
                    <i
                      className={`pi ${
                        sortOrder === 1
                          ? 'pi-sort-amount-up-alt'
                          : 'pi-sort-amount-down'
                      }`}
                      style={{ fontSize: '0.9rem', color: 'white' }}
                    />
                  )}
                  {column.headerTooltip && (
                    <i
                      className="pi pi-info-circle"
                      style={{ fontSize: '0.9rem' }}
                    />
                  )}
                </div>
              );
            };

            // Automatically wrap filter element with sort if showSortInFilter is true
            const getFilterElement = () => {
              if (!column.filterElement) {
                return undefined;
              }

              return (options) => {
                const filterContent = column.filterElement(options);

                // If column explicitly wants sort in filter and showSortInFilters is enabled
                if (column.showSortInFilter && showSortInFilters) {
                  // Use date-specific labels for date columns
                  const sortLabels = column.isDateColumn
                    ? DATE_SORT_LABELS()
                    : undefined;

                  return filterWithSortTemplate(
                    options,
                    filterContent,
                    column.value,
                    handleSort,
                    sortField,
                    sortOrder,
                    sortLabels,
                  );
                }

                return filterContent;
              };
            };

            return (
              <Column
                exportField={column.exportField}
                key={column.label}
                field={column.value}
                filterField={column.filterField}
                exportHeader={t(column.label)}
                headerStyle={column.headerStyle}
                header={
                  column.headerTemplate
                    ? column.headerTemplate()
                    : defaultHeader()
                }
                headerTooltip={column.headerTooltip}
                headerTooltipOptions={{ position: 'top' }}
                bodyClassName={column.isDateColumn ? 'date-column' : undefined}
                body={column.body}
                sortable={column.sortable}
                filter={column.filter}
                dataType={column.dataType}
                filterClear={filterClearTemplate}
                filterApply={filterApplyTemplate}
                filterElement={getFilterElement()}
                filterMatchMode={column.filterMatchMode}
                filterFunction={column.filterFunction}
                showFilterMatchModes={column.showFilterMatchModes ?? false}
                showFilterOperator={column.showFilterOperator ?? false}
                maxConstraints={column.maxConstraints ?? 1}
              />
            );
          })}
        </PrimeDataTable>
      </div>
    );
  },
);

export default React.memo(DataTable);
